import { Tooltip } from "antd";

import type { BillingInterval } from "store/slices/reduxApi/billing";

import { ReactComponent as ComponentIcon } from "../../assets/icons/common/component.svg";
import { ManagePlanQuantityStepper } from "./ManagePlanQuantityStepper";
import { ManagePlanUsageBar } from "./ManagePlanUsageBar";

type ManagePlanDeployPanelProps = {
  billingInterval: BillingInterval;
  deployedAppsIncluded: number;
  currentDeployedApps: number;
  currentDeployedAppsUsage: number;
  deployedAppCountUnavailable?: boolean;
  deployedApps: number;
  deployedAppPriceMonthly: number;
  deployedAppPriceAnnual: number;
  onDeployedAppsChange: (value: number) => void;
};

function formatUsd(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ManagePlanDeployPanel({
  billingInterval,
  deployedAppsIncluded,
  currentDeployedApps,
  currentDeployedAppsUsage,
  deployedAppCountUnavailable = false,
  deployedApps,
  deployedAppPriceMonthly,
  deployedAppPriceAnnual,
  onDeployedAppsChange,
}: ManagePlanDeployPanelProps) {
  const deployedAppPrice =
    billingInterval === "annual"
      ? deployedAppPriceAnnual
      : deployedAppPriceMonthly;
  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";
  const projectedTotalLimit = deployedAppsIncluded + deployedApps;
  const currentTotalLimit = deployedAppsIncluded + currentDeployedApps;
  const plural = (n: number) => (n === 1 ? "" : "s");
  const includedLine = `${deployedAppsIncluded} deployed app${plural(deployedAppsIncluded)} included with your plan`;
  const tooltipTitle = (
    <div>
      <div>{includedLine}</div>
      {currentDeployedApps > 0 ? (
        <div>{`${currentDeployedApps} additional deployed app${plural(currentDeployedApps)}`}</div>
      ) : null}
    </div>
  );

  return (
    <div
      data-test="manage-deploy-panel"
      className="flex flex-col overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_rgba(31,38,51,0.12)]"
    >
      <div className="flex items-center gap-2 border-b border-[#e8eaed] bg-[#f9fafb] px-4 py-3">
        <ComponentIcon className="h-4 w-4" />
        <span className="text-xs font-medium leading-4 text-[#1f2633]">
          Deployed Apps
        </span>
      </div>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs leading-4 text-[#6c7689]">Current plan</span>
          <span className="flex items-center gap-1.5 text-[13px] font-medium leading-5 text-[#1f2633]">
            <span>
              {currentTotalLimit} Deployed App{plural(currentTotalLimit)}
            </span>
            <Tooltip title={tooltipTitle} placement="top">
              <span className="flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-[#e8eaed] text-[10px] font-bold leading-none text-[#6c7689]">
                i
              </span>
            </Tooltip>
          </span>
        </div>

        <ManagePlanUsageBar
          used={currentDeployedAppsUsage}
          total={projectedTotalLimit}
        />

        <div className="h-0 border-b border-[#e8eaed]" />

        <div className="flex flex-col gap-3">
          <span className="text-xs leading-4 text-[#6c7689]">
            Add additional Deployed Apps ({formatUsd(deployedAppPrice)}/app
            {intervalSuffix})
          </span>
          <ManagePlanQuantityStepper
            value={deployedApps}
            min={Math.max(0, currentDeployedAppsUsage - deployedAppsIncluded)}
            disabled={deployedAppCountUnavailable}
            onChange={onDeployedAppsChange}
          />
        </div>
      </div>
    </div>
  );
}
